import { SerializableState } from './SerializableState'

export interface UserWaypointVisibilityStateProps {
  // Ids of user waypoints the user has turned OFF in the management screen. We store
  // the DISABLED set (opt-out), so an absent id means "enabled": newly created points
  // and points without a record show by default, matching the pre-existing behaviour.
  // Persisted (localStorage via StatePersister), so the choice survives a reload.
  disabledIds: string[]
}

const INITIAL_STATE: UserWaypointVisibilityStateProps = {
  disabledIds: []
}

// Per-point map visibility of user waypoints. The eye toggle in the management screen
// writes here; MapADHPPresenter reads it to filter disabled points out of the ADHP
// layer at render time. This is orthogonal to the toolbar layer switch (showUserWaypoints):
// a point shows only when the toolbar layer is on AND the point is enabled here.
export class UserWaypointVisibilityState extends SerializableState<UserWaypointVisibilityStateProps> {
  constructor() {
    super(INITIAL_STATE)
  }

  isDisabled(id: string): boolean {
    return this.getStateSnapshot().disabledIds.includes(id)
  }

  getDisabledIds(): string[] {
    return this.getStateSnapshot().disabledIds
  }

  setDisabled(id: string, disabled: boolean): void {
    const current = this.getStateSnapshot().disabledIds
    const isCurrentlyDisabled = current.includes(id)
    if (disabled === isCurrentlyDisabled) return

    const next = disabled ? [...current, id] : current.filter((disabledId) => disabledId !== id)
    this._updateStateProp('disabledIds', next)
  }

  toggle(id: string): void {
    this.setDisabled(id, !this.isDisabled(id))
  }

  // Batch toggle used by the management screen: sets every id to the same target
  // state in a single update (so subscribers re-render once).
  setManyDisabled(ids: string[], disabled: boolean): void {
    const current = this.getStateSnapshot().disabledIds
    const next = disabled
      ? Array.from(new Set([...current, ...ids]))
      : current.filter((disabledId) => !ids.includes(disabledId))
    if (next.length === current.length && next.every((id, index) => id === current[index])) return
    this._updateStateProp('disabledIds', next)
  }

  // Drops a point's override (e.g. when it is deleted), keeping the persisted list
  // from accumulating ids that no longer exist.
  clearWaypoint(id: string): void {
    this.setDisabled(id, false)
  }

  getKey(): string {
    return 'userWaypointVisibility'
  }
}
